import { Badge, Box, HStack, Text, VStack } from '@chakra-ui/react'
import { Encounter, SupplyLogEntry } from '@hb/shared'

// §6.7 "Billing team CDI view" — read-only. The provider answers CDI prompts (during
// charting / at pre-auth); the admin (billing) app only SEES the outcomes here. Shows
// HCPCS codes — this is the billing surface. No buttons: billing cannot answer for the
// provider. Groups: confirmed by provider, dismissed by provider, and pending provider
// response (pending isn't in the spec's list, but billing needs to see unanswered
// detections now that they can't answer them — judgment call, recorded in the work list).
const EntryRow = ({
  entry,
  color,
  strike = false,
}: {
  entry: SupplyLogEntry
  color: string
  strike?: boolean
}) => (
  <HStack key={entry.id} px={1} py={0.5} justify="space-between" align="center">
    <Box minW={0}>
      <Text
        fontSize="xs"
        color={color}
        textDecoration={strike ? 'line-through' : undefined}
        noOfLines={1}>
        {entry.matchedSupply}
      </Text>
      {entry.hcpcsCode ? (
        <Text fontSize="10px" color="gray.500">
          HCPCS: {entry.hcpcsCode}
        </Text>
      ) : null}
    </Box>
  </HStack>
)

const Group = ({
  title,
  entries,
  color,
  badgeScheme,
  badgeLabel,
  strike = false,
}: {
  title: string
  entries: SupplyLogEntry[]
  color: string
  badgeScheme: string
  badgeLabel: string
  strike?: boolean
}) => {
  if (entries.length === 0) return null
  return (
    <VStack align="stretch" spacing={0.5}>
      <HStack justify="space-between">
        <Text fontSize="xs" color="gray.400" fontWeight={500}>
          {title}
        </Text>
        <Badge colorScheme={badgeScheme} fontSize="10px">
          {badgeLabel}
        </Badge>
      </HStack>
      {entries.map(entry => (
        <EntryRow key={entry.id} entry={entry} color={color} strike={strike} />
      ))}
    </VStack>
  )
}

export const CdiSummary = ({ encounter }: { encounter: Encounter }) => {
  const supplyLog = encounter.supplyLog ?? []
  if (supplyLog.length === 0) return null

  const confirmed = supplyLog.filter(e => e.confirmedByProvider)
  const dismissed = supplyLog.filter(e => e.dismissedByProvider)
  const pending = supplyLog.filter(e => !e.confirmedByProvider && !e.dismissedByProvider)

  return (
    <VStack w="100%" align="stretch" spacing={2} px={2} py={1}>
      <Text fontSize="xs" fontWeight={600} color="gray.600">
        CDI Detections
      </Text>
      <Group
        title="CONFIRMED BY PROVIDER"
        entries={confirmed}
        color="green.700"
        badgeScheme="green"
        badgeLabel="Confirmed"
      />
      <Group
        title="DISMISSED BY PROVIDER"
        entries={dismissed}
        color="gray.400"
        badgeScheme="gray"
        badgeLabel="Dismissed"
        strike
      />
      <Group
        title="PENDING PROVIDER RESPONSE"
        entries={pending}
        color="orange.600"
        badgeScheme="orange"
        badgeLabel="Pending"
      />
    </VStack>
  )
}
